import type {
  Completion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete"
import { syntaxTree } from "@codemirror/language"
import type { SyntaxNode } from "@lezer/common"
import type { EditorState } from "@codemirror/state"
import {
  COMMON_KEYWORDS,
  JSON_STRING_FORMATS,
  JsonFieldType,
  KEYWORD_DESCRIPTIONS,
  NULL,
  TYPE_KEYWORDS,
} from "./model"

/**
 * Type-aware completion for a JsonField's property names and enum-valued fields.
 *
 * The bundled schema-driven completion cannot see our per-type keywords or their enum values:
 * it reads a schema's top-level `properties`, and ours live inside `if/then` branches it never
 * evaluates. Rather than flatten the meta-schema (which would offer every keyword for every
 * type), we drive completion from the same `TYPE_KEYWORDS` table `validateJsonField` enforces,
 * so the suggestions and the accepted set are one description of the contract. This is the sole
 * schema-aware completion source; it covers both property names and the `type`/`format` values.
 */

const unquote = (raw: string) => raw.replace(/^"/, "").replace(/"$/, "")

const propertyNameOf = (
  property: SyntaxNode,
  state: EditorState,
): string | undefined => {
  const name = property.getChild("PropertyName")

  return name ? unquote(state.sliceDoc(name.from, name.to)) : undefined
}

/** The keywords a field of `baseType` still accepts, minus the ones already written. */
export const availableKeywords = (
  baseType: JsonFieldType | undefined,
  present: ReadonlySet<string>,
): string[] => {
  const all = baseType
    ? [...COMMON_KEYWORDS, ...TYPE_KEYWORDS[baseType]]
    : [...COMMON_KEYWORDS]

  return all.filter((keyword) => !present.has(keyword))
}

const enclosingObject = (node: SyntaxNode): SyntaxNode | null => {
  for (
    let current: SyntaxNode | null = node;
    current;
    current = current.parent
  ) {
    if (current.name === "Object") {
      return current
    }
  }

  return null
}

const propertyKeys = (object: SyntaxNode, state: EditorState): string[] => {
  const keys: string[] = []
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") {
      continue
    }
    const name = property.getChild("PropertyName")
    if (name) {
      keys.push(unquote(state.sliceDoc(name.from, name.to)))
    }
  }

  return keys
}

const isBaseType = (value: string): value is JsonFieldType =>
  (Object.values(JsonFieldType) as string[]).includes(value)

/** Reads the object's declared `type`, tolerating the nullable `[T, "null"]` form. */
const declaredType = (
  object: SyntaxNode,
  state: EditorState,
): JsonFieldType | undefined => {
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") {
      continue
    }
    const name = property.getChild("PropertyName")
    if (!name || unquote(state.sliceDoc(name.from, name.to)) !== "type") {
      continue
    }

    // The value is the property's last child; a `:` token sits between it and the name.
    const value = property.lastChild
    if (!value || value === name) {
      return undefined
    }
    if (value.name === "String") {
      const literal = unquote(state.sliceDoc(value.from, value.to))

      return isBaseType(literal) ? literal : undefined
    }
    if (value.name === "Array") {
      for (let item = value.firstChild; item; item = item.nextSibling) {
        if (item.name !== "String") {
          continue
        }
        const literal = unquote(state.sliceDoc(item.from, item.to))
        if (isBaseType(literal)) {
          return literal
        }
      }
    }

    return undefined
  }

  return undefined
}

/**
 * Roles alternate down the tree: a JsonField (schema object) whose `properties` value is a
 * field-name map, whose values are themselves JsonFields, whose `properties` is a map, and so on.
 * `items` stays a JsonField. Classifying by ancestry — not just the parent key's name —
 * distinguishes the real `properties` record from a field a schema author happens to name
 * `properties`: the latter is a value inside a map, so it is a JsonField and still completes.
 */
type ObjectRole = "field" | "map" | "other"

const objectRole = (object: SyntaxNode, state: EditorState): ObjectRole => {
  const parent = object.parent
  if (!parent || parent.name === "JsonText") {
    return "field" // the root schema
  }
  if (parent.name !== "Property" || parent.parent?.name !== "Object") {
    return "other"
  }

  const container = objectRole(parent.parent, state)
  if (container === "map") {
    return "field" // any value in a properties map is a field definition
  }
  if (container === "field") {
    const key = propertyNameOf(parent, state)
    if (key === "properties") {
      return "map"
    }
    if (key === "items") {
      return "field"
    }
  }

  return "other"
}

/**
 * True when `object` is a `properties` record — a map whose keys are the schema author's own
 * field names, not JsonField keywords. Completing keywords there would create a field literally
 * named `type`, so we suppress it; the field definitions inside the map still complete.
 */
const isPropertiesMap = (object: SyntaxNode, state: EditorState): boolean =>
  objectRole(object, state) === "map"

/**
 * True when the cursor sits where an object property *key* goes — inside a property name being
 * typed, or on the empty run after `{` or a `,`. Value positions (after the colon) return false
 * so this source stays out of the way of value completion.
 */
const propertyNameContext = (
  node: SyntaxNode,
  pos: number,
  state: EditorState,
): { object: SyntaxNode; from: number; to: number } | null => {
  const contextForObject = (
    object: SyntaxNode | null,
    from: number,
    to: number,
  ) => (object && !isPropertiesMap(object, state) ? { object, from, to } : null)

  if (node.name === "PropertyName" || node.name === "⚠") {
    return contextForObject(
      node.parent && enclosingObject(node.parent),
      node.from,
      node.to,
    )
  }

  if (node.name === "Object") {
    return contextForObject(node, pos, pos)
  }

  return null
}

/**
 * When the cursor is inside a string value, the key it belongs to and whether that value sits
 * inside an array (the nullable `[T, "null"]` form). Value strings are `String` nodes — distinct
 * from `PropertyName` — so this never fires for keys.
 */
const valueContext = (
  node: SyntaxNode,
  state: EditorState,
): { key: string; inArray: boolean; from: number; to: number } | null => {
  if (node.name !== "String") {
    return null
  }
  const parent = node.parent
  if (parent?.name === "Property") {
    const key = propertyNameOf(parent, state)

    return key ? { key, inArray: false, from: node.from, to: node.to } : null
  }
  if (parent?.name === "Array" && parent.parent?.name === "Property") {
    const key = propertyNameOf(parent.parent, state)

    return key ? { key, inArray: true, from: node.from, to: node.to } : null
  }

  return null
}

/** The enum values offered for a `type` or `format` value; empty for any other key. */
const valueOptions = (key: string, inArray: boolean): string[] => {
  if (key === "type") {
    const types = Object.values(JsonFieldType)

    return inArray ? [...types, NULL] : types
  }
  if (key === "format") {
    return [...JSON_STRING_FORMATS]
  }

  return []
}

export const jsonFieldCompletion = (
  context: CompletionContext,
): CompletionResult | null => {
  const node = syntaxTree(context.state).resolveInner(context.pos, -1)

  const value = valueContext(node, context.state)
  if (value) {
    // `from` sits on the opening quote, so the text CodeMirror would match against labels
    // includes it; filter manually and disable the built-in filter, or every option is hidden.
    const typed = context.state.sliceDoc(value.from + 1, context.pos)
    const options = valueOptions(value.key, value.inArray)
      .filter((literal) => literal.startsWith(typed))
      .map((literal) => ({
        label: literal,
        type: "constant",
        apply: `"${literal}"`,
      }))

    return options.length
      ? { from: value.from, to: value.to, options, filter: false }
      : null
  }

  const place = propertyNameContext(node, context.pos, context.state)
  if (!place) {
    return null
  }

  // An open quoted key already being typed ("mi|) — replace from its opening quote.
  const quoted = context.matchBefore(/"[^"\\]*/)
  const word = context.matchBefore(/\w+/)
  if (!context.explicit && !quoted && !word) {
    return null
  }

  const from = quoted ? quoted.from : word ? word.from : place.from
  // Swallow a closing quote sitting under the cursor so we don't leave a stray one.
  const to =
    context.state.sliceDoc(context.pos, context.pos + 1) === '"'
      ? context.pos + 1
      : Math.max(context.pos, place.to)

  const typed = quoted
    ? context.state.sliceDoc(quoted.from + 1, context.pos)
    : (word?.text ?? "")

  const present = new Set(propertyKeys(place.object, context.state))
  present.delete(typed)

  // `from` covers the opening quote, so CodeMirror's built-in filter would match labels against
  // `"mi` and hide everything; filter by the bare prefix ourselves and turn the built-in off.
  const options: Completion[] = availableKeywords(
    declaredType(place.object, context.state),
    present,
  )
    .filter((keyword) => keyword.startsWith(typed))
    .map((keyword) => ({
      label: keyword,
      type: "property",
      info: KEYWORD_DESCRIPTIONS[keyword],
      apply: `"${keyword}": `,
    }))

  return options.length ? { from, to, options, filter: false } : null
}
